import {
  useMutation,
  UseQueryResult,
  useQueryClient
} from "@tanstack/react-query";
import { Applicant, Application } from "types/application";
import { PendingReportView } from "views/applications-id-report/components/ReportsFallback/PendingReportView";
import { ProgressReportView } from "views/applications-id-report/components/ReportsFallback/ProgressReportView";
import {
  ApplicantSettings,
  ApplicantType,
  Invitee,
  Verification,
  VerificationNames,
  VerificationStatus
} from "shared/types";
import { VerificationSchema } from "types/landlord/verificationTemplate";
import { TabStepsType } from "views/applications-id";
import { instance } from "api/apiBase";
import { logger } from "logger";
import { getErrorMessage } from "utils/error";
import { toast } from "react-toastify";

import { lazy, Suspense, useCallback, useMemo } from "react";

const AdditionalDocumentsSlice = lazy(
  () => import("../slices/AdditionalDocumentsSlice")
);
const ApplicantTabSlice = lazy(() =>
  import("../slices/ApplicantCardSlice/ApplicantTabSlice").then((m) => ({
    default: m.ApplicantTabSlice
  }))
);
const BasicInformation = lazy(() => import("../slices/BasicInformation"));
const CriminalReportSlice = lazy(() => import("../slices/CriminalReportSlice"));
const DisclosureSlice = lazy(() => import("../slices/DisclosureSlice"));
const EvictionReportSlice = lazy(() => import("../slices/EvictionReportSlice"));
const HousingHistoryReportSlice = lazy(
  () => import("../slices/HousingHistoryReportSlice")
);
const IncomeReportSlice = lazy(() => import("../slices/IncomeReportSlice"));
const WarningsReportSlice = lazy(() => import("../slices/WarningsReportSlice"));

import { ManualReportView } from "views/applications-id-report/components/ReportsFallback/ManualReportView";
import { ProcessingReportView } from "views/applications-id-report/components/ReportsFallback/ProcessingReportView";
import { SkippedReportView } from "views/applications-id-report/components/ReportsFallback/SkippedReportView";
import { CreditReportBlockedView } from "views/applications-id-report/components/ReportsFallback/CreditReportBlockedView";
import { UnavailableReportView } from "./ReportsFallback/UnavailableReportView";
import { CanadianCriminalReportSlice } from "views/applications-id-report/slices/CanadianCriminalReportSlice";
import { CanadianCreditReportSlice } from "views/applications-id-report/slices/CanadianCreditReportSlice";
import { Button, ButtonSize, ButtonVariant } from "shared/components/Button";
import { CombinedIncomeReportSlice } from "../slices/CombinedIncomeReportSlice";
import { PurchaseApplicationSlice } from "../slices/PurchaseApplication";
import { PetScreeningReportView } from "./ReportsFallback/PetScreeningReportView";
import { handleCopy } from "utils/handleCopy";
import { useAuthContext } from "providers/Auth";
import { NoReportAccessView } from "./ReportsFallback/NoReportAccessView";
import { ReportHeader } from "./ReportHeader";
import { verificationNameToTitle } from "views/applications-id/components/ApplicantsTable/utils";
import { CreditReportSliceWrapper } from "../slices/CreditReportSlice/components/CreditReportSliceWrapper";
import { IdentityVerificationReportSliceWrapper } from "../slices/IdentityVerificationReportSlice/components/IdentityVerificationReportSliceWrapper";
import { VerificationStateHandler } from "shared/utils/verification-state-handler";

interface ReportsCardProps {
  applicant?: Applicant;
  currentTab: TabStepsType;
  applicationMeta: UseQueryResult<Application | undefined, unknown>;
  requiresOverridesIdentityVerification: boolean;
  currentVerification?: Verification;
  screeningCriteria?: ApplicantSettings;
  verificationSchemas?: {
    [Key in VerificationNames]: VerificationSchema;
  };
  templateId?: string;
  allowCoSigner?: boolean;
  allowOccupant?: boolean;
  pendingMagicLink?: Invitee;
  onCancelClick?: (applicantId: string) => void;
  onUncancelClick?: (applicantId: string) => void;
  onChangeRoleClick?: (applicant: Applicant) => void;
  onSetPrimaryClick?: (applicant: Applicant) => void;
  onRefundClick?: (applicantId: string) => void;
  onResendInviteClick?: (invite: Invitee) => void;
  onDocumentRequest?: (applicantId: string) => void;
  verificationIds?: number[];
}

const cancelRetryErrorMessageMap = {
  already_completed:
    "Unable to cancel retry attempt because the applicant has already completed their identity verification"
};

export const ReportsCard = ({
  applicant,
  currentTab,
  applicationMeta,
  requiresOverridesIdentityVerification,
  currentVerification,
  screeningCriteria,
  verificationSchemas,
  templateId,
  allowCoSigner,
  allowOccupant,
  pendingMagicLink,
  onCancelClick,
  onUncancelClick,
  onChangeRoleClick,
  onSetPrimaryClick,
  onRefundClick,
  onResendInviteClick,
  onDocumentRequest,
  verificationIds
}: ReportsCardProps) => {
  const { permissions } = useAuthContext();
  const queryClient = useQueryClient();

  const isPermitted = useMemo(() => {
    switch (currentTab) {
      case VerificationNames.RentalApplication:
        return permissions.canAccessReports.rentalApplication;
      case VerificationNames.IdentityVerification:
        return permissions.canAccessReports.identityVerification;
      case VerificationNames.IncomeVerification:
      case VerificationNames.CombinedIncome:
      case VerificationNames.VerifiedIncomeVerification:
        return permissions.canAccessReports.incomeReport;
      case VerificationNames.HousingHistory:
        return permissions.canAccessReports.housingHistoryReport;
      case VerificationNames.CreditReport:
        return permissions.canAccessReports.creditReport;
      case VerificationNames.CriminalReport:
        return permissions.canAccessReports.usCriminalReport;
      case VerificationNames.EvictionReport:
        return permissions.canAccessReports.housingCourtReport;
      case VerificationNames.CanadianCriminalReport:
        return permissions.canAccessReports.caCriminalReport;
      case VerificationNames.CanadianCreditReport:
        return permissions.canAccessReports.caCreditReport;
      case VerificationNames.PetScreeningReport:
        return permissions.canAccessReports.animalScreening;
      case VerificationNames.SubmissionDisclosure:
        return permissions.canAccessReports.disclosure;
      case VerificationNames.WarningsReport:
        return permissions.canAccessReports.creditReport;
      case VerificationNames.BoomFinanceVerification:
        return true;
      case "additional_documents":
        return permissions.canAccessReports.additionalDocumentsReport;
      case "applicant":
        return true;
      default:
        return false;
    }
  }, [currentVerification, permissions]);

  const refetchApplication = useCallback(() => {
    queryClient
      .refetchQueries({
        queryKey: ["APPLICATION", String(applicationMeta.data?.id)]
      })
      .catch((error) => {
        logger.error("Failed to invalidate queries", error);
      });
  }, [applicationMeta.data, queryClient]);

  const { mutate: createRetryLink, isPending: createRetryLinkPending } =
    useMutation({
      mutationKey: [
        "APPLICATION",
        applicationMeta.data?.id,
        applicant?.id,
        "RETRY_VERIFICATION_LINK"
      ],
      mutationFn: () =>
        instance.post<string>(
          `/portal/v1/applications/${applicationMeta.data?.id}/applicants/${applicant?.id}/create_retry_identity_verification_link`
        ),
      onSuccess: ({ data }) => {
        refetchApplication();
        handleCopy(data, "The link has been copied to your clipboard");
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        toast.error(
          `Request identity verification link failed: ${errorMessage}`
        );
      }
    });

  const { mutate: cancelRetryIdentityVerification, isPending } = useMutation({
    mutationFn: () =>
      instance.post(
        `/portal/v1/applications/${applicationMeta.data?.id}/applicants/${applicant?.id}/cancel_request_retry_identity_verification`
      ),
    onSuccess: () => {
      refetchApplication();
      toast.success("Identity verification retry successfully canceled");
    },
    onError: (error) => {
      const errorMessage = getErrorMessage(error);

      if (
        ["not_request_retry_yet", "already_completed"].includes(errorMessage)
      ) {
        refetchApplication();

        if (errorMessage === "not_request_retry_yet") {
          toast.success("Identity verification retry successfully canceled");
          return;
        }
      }

      toast.error(
        cancelRetryErrorMessageMap[errorMessage] ||
          `Cancel identity verification retry failed: ${errorMessage}`
      );
    }
  });

  if (!applicant || !applicationMeta.data) return null;

  if (!isPermitted) return <NoReportAccessView />;

  const verificationId = Number(currentVerification?.id);

  const isIdentityVerificationOverrideRequired =
    applicationMeta.data.requires_overrides_identity_verification?.applicants.some(
      ({ id }) => id === applicant.id
    );

  const { getReportState } = new VerificationStateHandler({
    verification: currentVerification,
    applicant: applicant,
    application: applicationMeta.data
  });
  const reportState = getReportState({ isRender: true });

  switch (reportState) {
    case "render_credit_report":
      return (
        <Suspense fallback={null}>
          <CreditReportSliceWrapper
            key={applicant.id}
            applicant={applicant}
            defaultVerificationId={verificationId}
            screeningCriteria={screeningCriteria}
            applicationMeta={applicationMeta}
            isIdentityVerificationOverrideRequired={Boolean(
              isIdentityVerificationOverrideRequired
            )}
          />
        </Suspense>
      );
    case "render_warnings_report":
      return (
        <Suspense fallback={null}>
          <WarningsReportSlice
            applicant={applicant}
            verificationId={verificationId}
            screeningStatus={applicationMeta.data.screening_status}
          />
        </Suspense>
      );
    case "idv_override_required":
      return (
        <PendingReportView message="Please review issues with applicant's identity verification report. This report will not run until identity is verified" />
      );
    case "pending":
      return <PendingReportView />;
    case "canceled":
      return <PendingReportView message="Report was canceled" />;
    case "started":
      return <ProgressReportView />;
    case "no_data_available":
      return <UnavailableReportView />;
    case "finished":
    case "failed":
    case "finished_with_warnings":
    case "skipped":
    case "render_report":
      switch (currentTab) {
        case VerificationNames.RentalApplication:
          return (
            <Suspense fallback={null}>
              <BasicInformation
                verificationId={verificationId}
                applicant={applicant}
                applicationMeta={applicationMeta}
                email={applicant.email}
                phone={applicant.phone}
              />
            </Suspense>
          );
        case VerificationNames.CanadianCreditReport:
          return (
            <Suspense fallback={null}>
              <CanadianCreditReportSlice
                applicant={applicant}
                defaultVerificationId={verificationId}
                isNotResident={
                  currentVerification?.status === VerificationStatus.Skipped
                }
                screeningCriteria={screeningCriteria}
                applicationMeta={applicationMeta}
                isIdentityVerificationOverrideRequired={Boolean(
                  isIdentityVerificationOverrideRequired
                )}
              />
            </Suspense>
          );
        case VerificationNames.CanadianCriminalReport:
          return (
            <Suspense fallback={null}>
              <CanadianCriminalReportSlice
                applicationId={String(applicationMeta.data.id)}
                applicant={applicant}
                isNotResident={
                  currentVerification?.status === VerificationStatus.Skipped
                }
              />
            </Suspense>
          );
        case VerificationNames.IdentityVerification:
          return (
            <IdentityVerificationReportSliceWrapper
              key={applicant.id}
              applicant={applicant}
              application={applicationMeta.data}
              defaultVerificationId={verificationId}
              requiresOverridesIdentityVerification={
                requiresOverridesIdentityVerification
              }
              templateTemplateSettings={
                applicationMeta.data.verification_template?.settings
              }
              isVerificationInProgress={false}
            />
          );
        case VerificationNames.IncomeVerification:
        case VerificationNames.VerifiedIncomeVerification:
          return (
            <Suspense fallback={null}>
              <IncomeReportSlice
                applicationStatus={applicationMeta.data.status}
                applicant={applicant}
                screeningCriteria={screeningCriteria}
                verificationTemplate={
                  applicationMeta.data.verification_template
                }
                templateRecommendationSettings={
                  applicationMeta.data.verification_template
                    ?.recommendation_method
                }
              />
            </Suspense>
          );
        case VerificationNames.HousingHistory:
          return (
            <Suspense fallback={null}>
              <HousingHistoryReportSlice
                applicationId={applicationMeta.data.id}
                applicationStatus={applicationMeta.data.status}
                verificationId={verificationId}
                applicant={applicant}
                verificationSchemas={verificationSchemas}
              />
            </Suspense>
          );
        case VerificationNames.BoomFinanceVerification:
          return (
            <Suspense fallback={null}>
              <PurchaseApplicationSlice
                applicant={applicant}
                verificationId={verificationId}
                settings={
                  applicationMeta.data.verification_template
                    ?.verification_schemas[
                    applicant.role || ApplicantType.Applicant
                  ]?.[VerificationNames.BoomFinanceVerification]
                }
              />
            </Suspense>
          );
        case VerificationNames.EvictionReport:
          return (
            <Suspense fallback={null}>
              <EvictionReportSlice
                applicationStatus={applicationMeta.data.status}
                verificationId={verificationId}
                applicant={applicant}
                screeningCriteria={screeningCriteria}
              />
            </Suspense>
          );
        case VerificationNames.CriminalReport:
          return (
            <Suspense fallback={null}>
              <CriminalReportSlice
                applicationId={applicationMeta.data.id}
                applicationStatus={applicationMeta.data.status}
                verificationId={verificationId}
                applicant={applicant}
                screeningCriteria={screeningCriteria}
                allowedManualFips={[
                  ...(applicationMeta.data.verification_template?.settings[
                    applicant.role || ApplicantType.Applicant
                  ]?.criminal_report?.manual_walkin_county_fips || []),
                  ...(applicationMeta.data.verification_template?.settings[
                    applicant.role || ApplicantType.Applicant
                  ]?.criminal_report?.manual_walkin_statewide_fips || [])
                ]}
                manualSearchAllowed={Boolean(
                  applicationMeta.data.verification_template?.settings[
                    applicant.role || ApplicantType.Applicant
                  ]?.criminal_report?.allow_manual_county_search &&
                  applicationMeta.data.verification_template?.settings[
                    applicant.role || ApplicantType.Applicant
                  ]?.criminal_report?.allow_manual_statewide_search
                )}
              />
            </Suspense>
          );
        case "additional_documents":
          return (
            <Suspense fallback={null}>
              <AdditionalDocumentsSlice applicant={applicant} />
            </Suspense>
          );
        case VerificationNames.SubmissionDisclosure: {
          const jsonSchema = currentVerification?.steps?.[0].json_schema as
            | { partner_name_in_submission_disclosure?: string }
            | undefined;

          return (
            <Suspense fallback={null}>
              <DisclosureSlice
                applicationStatus={applicationMeta.data.status}
                applicant={applicant}
                verificationId={verificationId}
                partnerNameInSubmissionDisclosure={
                  jsonSchema?.partner_name_in_submission_disclosure ||
                  applicationMeta.data.partner_name_in_submission_disclosure
                }
              />
            </Suspense>
          );
        }
        case "applicant":
          return (
            <Suspense fallback={null}>
              <ApplicantTabSlice
                applicant={applicant}
                applicationStatus={applicationMeta.data.status}
                templateId={templateId}
                allowCoSigner={allowCoSigner ?? false}
                allowOccupant={allowOccupant ?? false}
                pendingMagicLink={pendingMagicLink}
                onCancelClick={onCancelClick}
                onUncancelClick={onUncancelClick}
                onChangeRoleClick={onChangeRoleClick}
                onSetPrimaryClick={onSetPrimaryClick}
                onRefundClick={onRefundClick}
                onResendInviteClick={onResendInviteClick}
                onDocumentRequest={onDocumentRequest}
                verificationIds={verificationIds ?? []}
              />
            </Suspense>
          );
      }
  }

  // For reportStates that depend on currentVerification
  if (!currentVerification) return null;

  switch (reportState) {
    case "render_combined_income_report":
      return (
        <Suspense fallback={null}>
          <CombinedIncomeReportSlice
            key={applicant.id}
            applicationStatus={applicationMeta.data.status}
            applicant={applicant}
            hasCreditReport={applicant.verifications.some(
              (v) => v.name === VerificationNames.CreditReport
            )}
            applicantId={applicant.id}
            verificationId={verificationId}
            applicationId={applicant.application_id}
            activeVerification={currentVerification}
            applicationMeta={applicationMeta}
          />
        </Suspense>
      );
    case "idv_retrying":
      // A re-run keeps the prior reports around, so the partner still needs the
      // header and version tabs while the new request is outstanding.
      if (currentVerification?.is_rerun)
        return (
          <IdentityVerificationReportSliceWrapper
            key={applicant.id}
            applicant={applicant}
            application={applicationMeta.data}
            defaultVerificationId={verificationId}
            requiresOverridesIdentityVerification={
              requiresOverridesIdentityVerification
            }
            templateTemplateSettings={
              applicationMeta.data.verification_template?.settings
            }
            isVerificationInProgress
          />
        );

      return (
        <div className="h-full flex flex-col items-center justify-center">
          <div className="flex flex-col items-center">
            <ProcessingReportView
              className="!max-w-lg"
              currentVerification={currentVerification}
              applicant={applicant}
            />
            <div className="flex gap-2">
              <Button
                size={ButtonSize.BASE}
                variant={ButtonVariant.BASE}
                onClick={() => createRetryLink()}
                loading={createRetryLinkPending}
                disabled={createRetryLinkPending}
              >
                Copy retry link
              </Button>

              <Button
                size={ButtonSize.BASE}
                variant={ButtonVariant.PRIMARY}
                onClick={() => cancelRetryIdentityVerification()}
                loading={isPending}
                disabled={isPending}
              >
                Cancel retry
              </Button>
            </div>
          </div>
        </div>
      );
    case "manual_pending":
      return (
        <>
          <ReportHeader
            applicationStatus={applicationMeta.data.status}
            applicantId={applicant.id}
            applicationId={applicationMeta.data.id}
            title={verificationNameToTitle(currentVerification.name)}
            name={currentVerification.name}
            hidePrint
          />
          <ManualReportView
            refetch={applicationMeta.refetch}
            applicantStatus={
              applicationMeta.data.applicants.find(
                (appl) => appl.id === applicant.id
              )?.application_status
            }
            applicationStatus={applicationMeta.data.status}
            applicantId={applicant.id}
            verificationId={currentVerification.id}
            applicationId={applicationMeta.data.id}
            verificationName={currentVerification.name}
            creditReportStatus={
              applicant.verifications.find(
                (v) => v.name === VerificationNames.CreditReport
              )?.status
            }
          />
        </>
      );
    case "conditional_pending":
    case "conditional_skipped":
      return (
        <SkippedReportView
          refetch={applicationMeta.refetch}
          applicantId={applicant.id}
          verificationId={currentVerification.id}
          applicationId={applicationMeta.data.id}
          verificationStatus={currentVerification.status}
        />
      );
    case "eviction_blocked_by_credit":
      return (
        <>
          <ReportHeader
            applicationStatus={applicationMeta.data.status}
            applicantId={applicant.id}
            applicationId={applicationMeta.data.id}
            title={verificationNameToTitle(currentVerification.name)}
            name={currentVerification.name}
            hidePrint
          />
          <CreditReportBlockedView
            refetch={applicationMeta.refetch}
            applicantId={applicant.id}
            verificationId={currentVerification.id}
            applicationId={applicationMeta.data.id}
          />
        </>
      );
    case "rerun_in_progress":
      return (
        <ProgressReportView message="Report re-run in progress. Please wait" />
      );
    case "submitted_in_progress":
      return <ProgressReportView />;
    case "processing":
      return <ProcessingReportView currentVerification={currentVerification} />;
    case "render_pet_screening":
      return (
        <PetScreeningReportView
          applicationStatus={applicationMeta.data.status}
          verification={currentVerification}
          applicant={applicant}
        />
      );
  }

  return null;
};
